<template>
  <div
    class="c-template-management-toolbar flex flex-wrap gap-2 pa-4 rounded-2 bg-base"
    :class="{
      'flex-col gap-2': isTemplateFolderEnabled,
      'justify-between': !isTemplateFolderEnabled,
    }"
  >
    <div v-if="isTemplateFolderEnabled" class="flex justify-between items-center w-full">
      <NBreadcrumb class="overflow-x-auto" separator=">">
        <NBreadcrumbItem
          v-for="(b, idx) in breadcrumbs"
          :key="b.id"
          class="content__breadcrumb__item text-lg color-primary"
          :class="{ 'cursor-pointer': idx !== breadcrumbs.length - 1 }"
          :clickable="idx !== breadcrumbs.length - 1"
          :style="{
            '--n-font-size': '18px',
          }"
          @click="onBreadcrumbClick(b.id)"
        >
          {{ b.name }}
        </NBreadcrumbItem>
      </NBreadcrumb>
      <div class="flex gap-4">
        <div class="flex gap-2">
          <CButton
            v-if="canvasType !== 'section-template'"
            v-tooltip="t('canvasUI.components.templates.CTemplateManagementToolbar.switchToTableView')"
            circle
            @click="$emit('toggle-view-mode')"
          >
            <template #icon>
              <CIcon icon="table" size="16px" />
            </template>
          </CButton>
          <CDropdown v-if="isTemplateFolderEnabled" :options="sortOptions" trigger="click" @select="onSortSelect">
            <CButton type="default">
              {{ currentSortLabel }}
              <template #icon>
                <CIcon :icon="currentSortIcon" size="16px" />
              </template>
            </CButton>
          </CDropdown>
        </div>
        <CTableColumnsSettings
          v-if="!hideView && !isTemplateFolderEnabled"
          :table-columns-settings="tableColumnsSettings"
          :tooltip-text="t('canvasUI.components.templates.CTemplateManagementToolbar.configureColumns')"
          @move="(payload) => emit('move', payload)"
          @restore="$emit('restoreColumns')"
          @select="(payload) => emit('select', payload)"
        />
        <CDropdown v-if="!hideCreate" :options="actionsMenuOptions" trigger="click" @select="executeAction">
          <CButton type="primary">
            {{ t('canvasUI.common.new') }}
            <template #icon>
              <CIcon icon="plus" size="16px" />
            </template>
          </CButton>
        </CDropdown>
      </div>
    </div>
    <div v-if="!isTemplateFolderEnabled" class="w-full flex justify-between items-start">
      <div class="flex-1 flex flex-wrap items-center gap-2 mr-4">
        <CSearchFilters
          ref="searchFiltersRef"
          :customFilterComponents="customFilterComponents"
          :disabled="!!isLoadingItems"
          entity="canvas"
          :filters="metadataFilterValues"
          :hide-metadata-filters="hideMetadataFilters"
          :include-draft-values="pitcherInfo?.isAnyTypeOfAdmin"
          :initial-search-value="search"
          :is-section="canvasType === 'section-template'"
          is-template
          :placeholder="t('canvasUI.components.templates.CTemplateManagementToolbar.searchInNameOrTags')"
          :tags-not-in="excludedTags"
          v-bind="popularityProps"
          @update:filters="emit('update:filter-values', $event)"
          @update:search="emit('search', $event)"
        />
      </div>
      <div class="flex gap-4 shrink-0">
        <CButton
          v-if="launchDarkly.enable_template_folders && canvasType !== 'section-template'"
          v-tooltip="t('canvasUI.components.templates.CTemplateManagementToolbar.switchToListView')"
          circle
          @click="$emit('toggle-view-mode')"
        >
          <template #icon>
            <CIcon icon="list" size="16px" />
          </template>
        </CButton>
        <CTableColumnsSettings
          v-if="!hideView"
          :table-columns-settings="tableColumnsSettings"
          :tooltip-text="t('canvasUI.components.templates.CTemplateManagementToolbar.configureColumns')"
          @move="(payload) => emit('move', payload)"
          @restore="$emit('restoreColumns')"
          @select="(payload) => emit('select', payload)"
        />

        <CDropdown
          v-if="actionsMenuOptions.length > 1"
          :options="actionsMenuOptions"
          trigger="click"
          @select="executeAction($event)"
        >
          <CButton icon-placement="left" type="primary">
            <template #icon>
              <CIcon icon="plus" size="16px" />
            </template>

            {{ t('canvasUI.components.templates.CTemplateManagementToolbar.new') }}
          </CButton>
        </CDropdown>

        <CButton
          v-else-if="actionsMenu.length === 1"
          icon-placement="left"
          type="primary"
          @click="executeAction(actionsMenu[0].key)"
        >
          <template #icon>
            <CIcon icon="plus" size="16px" />
          </template>

          {{ actionsMenu[0].label }}
        </CButton>
      </div>
    </div>
    <div v-else class="flex flex-wrap items-center gap-2 w-full">
      <CSearchFilters
        ref="searchFiltersRef"
        :customFilterComponents="customFilterComponents"
        :disabled="!!isLoadingItems"
        entity="canvas"
        :filters="metadataFilterValues"
        :hide-metadata-filters="hideMetadataFilters"
        :include-draft-values="pitcherInfo?.isAnyTypeOfAdmin"
        :initial-search-value="search"
        :is-section="canvasType === 'section-template'"
        is-template
        :placeholder="t('canvasUI.components.templates.CTemplateManagementToolbar.searchInNameOrTags')"
        :tags-not-in="excludedTags"
        v-bind="popularityProps"
        @update:filters="emit('update:filter-values', $event)"
        @update:search="emit('search', $event)"
      />
    </div>
  </div>
</template>
<script setup lang="tsx">
import { NBreadcrumb, NBreadcrumbItem } from 'naive-ui'
import { computed, inject, ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTableColumnsSettings, {
  type TableColumnSetting,
} from '@lib/components/CTableColumnsSettings/CTableColumnsSettings.vue'
import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { MetadataTemplateFieldTypeEnum } from '@openapi'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'
import tooltipDirective from '@lib/directives/tooltip'
import { type CanvasType } from '@lib/types/canvases'
import type { PitcherInfo } from '@lib/types/app'

const vTooltip = tooltipDirective

const props = withDefaults(
  defineProps<{
    breadcrumbs: { id: string; name: string }[]
    canvasType: Exclude<CanvasType, 'section' | 'canvas' | 'block'>
    search: string
    metadataFilterValues: Record<string, any>
    recommendedIds: string[]
    tableColumnsSettings: TableColumnSetting[]
    customFilterComponents: {
      type: MetadataTemplateFieldTypeEnum
      name: string
      label: string
      options?: { label: string; value: string }[]
    }[]
    hideCreate?: boolean
    hideConvert?: boolean
    hideView?: boolean
    isLoadingItems?: boolean
    isTemplateFolderEnabled?: boolean
    currentSort?: { field: string; order: 'asc' | 'desc' }
    viewMode?: 'table' | 'list'
    excludedTags?: string[]
  }>(),
  {
    breadcrumbs: () => [],
    currentSort: () => ({ field: 'name', order: 'asc' }),
    excludedTags: () => [],
  },
)

type Emit = {
  search: [s: string]
  'create:template': []
  'create:folder': []
  convert: []
  'update:filter-values': [mf: Record<string, any>]
  'breadcrumb:click': [id: string | null]
  move: [payload: { targetItemId: TableColumnSetting['id']; itemId: string }]
  select: [payload: { id: TableColumnSetting['id']; checked: boolean }]
  restoreColumns: []
  'sort-changed': [sort: { field: string; order: 'asc' | 'desc' }]
  'toggle-view-mode': []
}

const emit = defineEmits<Emit>()

type ActionsMenuOption = {
  label: string
  icon: string
  key: 'convert' | 'create:template' | 'create:folder'
}

const searchFiltersRef = ref<InstanceType<typeof CSearchFilters> | null>(null)

const { t } = useI18n()
const { engagingSections, popularSections } = useSectionPopularity()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)

const executeAction = (key: ActionsMenuOption['key']) => {
  emit(key as any)
}

const hideMetadataFilters = computed(
  () => pitcherInfo.value?.platform === 'impact' || !launchDarkly.value.show_metadata_filters_in_all_canvas_things,
)

const popularityProps = computed(() => ({
  engagingIds: launchDarkly.value.show_popularity_icons ? engagingSections.value.map((item) => item.id) : undefined,
  popularIds: launchDarkly.value.show_popularity_icons ? popularSections.value.map((item) => item.id) : undefined,
  recommendedIds: props.recommendedIds ?? [],
  showLatestFilter: false,
}))

function clearSearch() {
  searchFiltersRef.value?.clearSearch()
}

function onBreadcrumbClick(id: string) {
  if (id === props.breadcrumbs[props.breadcrumbs.length - 1]?.id) return
  emit('breadcrumb:click', id || null)
}

const actionsMenu = computed<ActionsMenuOption[]>(() => {
  const options: ActionsMenuOption[] = []

  if (!props.hideCreate) {
    options.push({
      label: t('canvasUI.components.templates.CTemplateManagementToolbar.createTemplate'),
      icon: 'circle-plus',
      key: 'create:template' as const,
    })
  }

  if (props.isTemplateFolderEnabled && !props.hideCreate) {
    options.push({
      label: t('canvasUI.components.templates.CTemplateManagementToolbar.createFolder'),
      icon: 'folder-plus',
      key: 'create:folder' as const,
    })
  }

  if (!props.hideConvert) {
    options.push({
      label: t('canvasUI.components.templates.CTemplateManagementToolbar.selectAndConvert'),
      icon: 'upload',
      key: 'convert' as const,
    })
  }

  return options
})

const actionsMenuOptions = computed<DropdownMixedOption[]>(() =>
  actionsMenu.value.map((item) => ({
    label: () => <div class="font-size-4">{item.label}</div>,
    key: item.key,
    icon: () => <CIcon icon={item.icon} />,
  })),
)

// Define sort options with their corresponding icons and labels in a single place
const sortOptionConfig = {
  name: {
    asc: {
      key: 'name_asc',
      icon: 'arrow-down-a-z',
    },
    desc: {
      key: 'name_desc',
      icon: 'arrow-down-z-a',
    },
  },
  modified_at: {
    asc: {
      key: 'modified_at_asc',
      icon: 'calendar-arrow-up',
    },
    desc: {
      key: 'modified_at_desc',
      icon: 'calendar-arrow-down',
    },
  },
}

const currentSortKey = computed(() => `${props.currentSort?.field || 'name'}_${props.currentSort?.order || 'asc'}`)

const currentSortIcon = computed(() => {
  // Find the last underscore to properly separate field from order
  const lastUnderscoreIndex = currentSortKey.value.lastIndexOf('_')
  const field = currentSortKey.value.substring(0, lastUnderscoreIndex)
  const order = currentSortKey.value.substring(lastUnderscoreIndex + 1) as 'asc' | 'desc'

  return sortOptionConfig[field as keyof typeof sortOptionConfig]?.[order]?.icon || 'arrow-down-wide-short'
})

const currentSortLabel = computed(() => {
  // Find the last underscore to properly separate field from order
  const lastUnderscoreIndex = currentSortKey.value.lastIndexOf('_')
  const field = currentSortKey.value.substring(0, lastUnderscoreIndex)
  const order = currentSortKey.value.substring(lastUnderscoreIndex + 1) as 'asc' | 'desc'

  // Use direct t() calls for each possible combination
  if (field === 'name' && order === 'asc') {
    return `${t('canvasUI.common.sortBy')}: ${t('canvasUI.components.templates.CTemplateManagementToolbar.name')} (${t('canvasUI.common.asc')})`
  } else if (field === 'name' && order === 'desc') {
    return `${t('canvasUI.common.sortBy')}: ${t('canvasUI.components.templates.CTemplateManagementToolbar.name')} (${t('canvasUI.common.desc')})`
  } else if (field === 'modified_at' && order === 'asc') {
    return `${t('canvasUI.common.sortBy')}: ${t('canvasUI.components.templates.CTemplateManagementToolbar.modifiedDate')} (${t('canvasUI.common.asc')})`
  } else if (field === 'modified_at' && order === 'desc') {
    return `${t('canvasUI.common.sortBy')}: ${t('canvasUI.components.templates.CTemplateManagementToolbar.modifiedDate')} (${t('canvasUI.common.desc')})`
  }

  // Fallback
  return t('canvasUI.common.sort')
})

const sortOptions = computed<DropdownMixedOption[]>(() => [
  {
    label: () => (
      <div class="font-size-4">{t('canvasUI.components.templates.CTemplateManagementToolbar.sortByNameAsc')}</div>
    ),
    key: sortOptionConfig.name.asc.key,
    icon: () => <CIcon icon={sortOptionConfig.name.asc.icon} />,
  },
  {
    label: () => (
      <div class="font-size-4">{t('canvasUI.components.templates.CTemplateManagementToolbar.sortByNameDesc')}</div>
    ),
    key: sortOptionConfig.name.desc.key,
    icon: () => <CIcon icon={sortOptionConfig.name.desc.icon} />,
  },
  {
    label: () => (
      <div class="font-size-4">{t('canvasUI.components.templates.CTemplateManagementToolbar.sortByModifiedAsc')}</div>
    ),
    key: sortOptionConfig.modified_at.asc.key,
    icon: () => <CIcon icon={sortOptionConfig.modified_at.asc.icon} />,
  },
  {
    label: () => (
      <div class="font-size-4">{t('canvasUI.components.templates.CTemplateManagementToolbar.sortByModifiedDesc')}</div>
    ),
    key: sortOptionConfig.modified_at.desc.key,
    icon: () => <CIcon icon={sortOptionConfig.modified_at.desc.icon} />,
  },
])

function onSortSelect(key: string) {
  // Find the last underscore to properly separate field from order
  const lastUnderscoreIndex = key.lastIndexOf('_')
  const field = key.substring(0, lastUnderscoreIndex)
  const order = key.substring(lastUnderscoreIndex + 1) as 'asc' | 'desc'

  emit('sort-changed', { field, order })
}

defineExpose({
  clearSearch,
})
</script>
